import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { StorageService } from '../../../../services/storage.service';
import { FormEditMode } from '../dynamic-form/interfaces/dynamic-field.interface';
import { BehaviorSubject, Observable, debounceTime, distinctUntilChanged } from 'rxjs';

// ========================================
// INTERFACES
// ========================================

export interface FormDataConfig {
    formName: string;
    enableAutoSave?: boolean;
    autoSaveDelay?: number;
    editMode?: FormEditMode;
    initialData?: any;
}

export interface SavedFormData {
    formData: any;
    timestamp: string;
    metadata?: {
        formName: string;
        path: string;
        version?: string;
    };
}

export interface AutoSaveOptions {
    enabled: boolean;
    delay: number;
    debounceTime: number;
}

// ========================================
// SERVIÇO PRINCIPAL
// ========================================

@Injectable({
    providedIn: 'root',
})
export class FormDataManagerService {
    private readonly storageService = inject(StorageService);
    private readonly router = inject(Router);

    // ========================================
    // MÉTODOS PÚBLICOS
    // ========================================

    /**
     * Configura e inicializa o gerenciador de dados
     */
    setupFormDataManager(config: FormDataConfig): {
        storageKey: string;
        autoSaveOptions: AutoSaveOptions;
        shouldRestoreData: boolean;
        shouldDisableAutoSave: boolean;
    } {
        const storageKey = this.generateStorageKey(config.formName);
        const autoSaveOptions = this.getAutoSaveOptions(config);
        const shouldRestoreData = this.shouldRestoreData(config);
        const shouldDisableAutoSave = this.shouldDisableAutoSave(config);

        return {
            storageKey,
            autoSaveOptions,
            shouldRestoreData,
            shouldDisableAutoSave,
        };
    }

    /**
     * Restaura dados salvos do storage
     */
    restoreData(storageKey: string): SavedFormData | null {
        try {
            const savedData = this.storageService.get<SavedFormData>(storageKey);

            if (savedData && this.isValidSavedData(savedData)) {
                return savedData;
            }

            return null;
        } catch (error) {
            console.error(`[FormDataManager] Erro ao restaurar dados: ${storageKey}`, error);
            return null;
        }
    }

    /**
     * Salva dados no storage
     */
    saveData(storageKey: string, formData: any, metadata?: any): void {
        try {
            const dataToSave: SavedFormData = {
                formData,
                timestamp: new Date().toISOString(),
                metadata: {
                    formName: this.extractFormNameFromKey(storageKey),
                    path: this.router.url,
                    version: '1.0',
                    ...metadata,
                },
            };

            this.storageService.set(storageKey, dataToSave);
        } catch (error) {
            console.error(`[FormDataManager] Erro ao salvar dados: ${storageKey}`, error);
        }
    }

    /**
     * Limpa dados salvos
     */
    clearData(storageKey: string): void {
        try {
            this.storageService.remove(storageKey);
        } catch (error) {
            console.error(`[FormDataManager] Erro ao limpar dados: ${storageKey}`, error);
        }
    }

    /**
     * Verifica se deve restaurar dados
     */
    shouldRestoreData(config: FormDataConfig): boolean {
        // Não restaura se auto-save estiver desabilitado
        if (!config.enableAutoSave) {
            return false;
        }

        // Não restaura se estiver em modo de edição com dados iniciais
        if (config.editMode?.active && config.initialData) {
            return false;
        }

        return true;
    }

    /**
     * Verifica se deve desabilitar auto-save
     */
    shouldDisableAutoSave(config: FormDataConfig): boolean {
        // Desabilita se auto-save estiver desabilitado
        if (!config.enableAutoSave) {
            return true;
        }

        // Desabilita se estiver em modo de edição
        if (config.editMode?.active) {
            return true;
        }

        return false;
    }

    /**
     * Verifica se campo deve ser readonly
     */
    isFieldReadonly(editMode?: FormEditMode): boolean {
        return editMode?.active && editMode?.action === 'view' ? true : false;
    }

    /**
     * Cria observable para auto-save com debounce
     */
    createAutoSaveObservable<T>(dataSource: Observable<T>, options: AutoSaveOptions): Observable<T> {
        if (!options.enabled) {
            return dataSource;
        }

        return dataSource.pipe(debounceTime(options.debounceTime), distinctUntilChanged());
    }

    /**
     * Verifica se há dados salvos
     */
    hasData(storageKey: string): boolean {
        try {
            const savedData = localStorage.getItem(storageKey);
            return savedData !== null;
        } catch (error) {
            console.error(`[FormDataManager] Erro ao verificar dados:`, error);
            return false;
        }
    }

    /**
     * Valida dados salvos
     */
    isValidSavedData(data: any): data is SavedFormData {
        return data && typeof data === 'object' && 'formData' in data && 'timestamp' in data && typeof data.timestamp === 'string';
    }

    /**
     * Obtém estatísticas de dados salvos
     */
    getStorageStats(): {
        totalKeys: number;
        totalSize: number;
        keys: string[];
    } {
        try {
            const keys: string[] = [];
            let totalSize = 0;

            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key && (key.startsWith('form_') || key.startsWith('checklist_'))) {
                    keys.push(key);
                    const value = localStorage.getItem(key);
                    if (value) {
                        totalSize += new Blob([value]).size;
                    }
                }
            }

            return {
                totalKeys: keys.length,
                totalSize,
                keys,
            };
        } catch (error) {
            console.error('[FormDataManager] Erro ao obter estatísticas:', error);
            return { totalKeys: 0, totalSize: 0, keys: [] };
        }
    }

    /**
     * Limpa todos os dados de formulários e checklists
     */
    clearAllFormData(): void {
        try {
            const stats = this.getStorageStats();
            stats.keys.forEach(key => {
                this.storageService.remove(key);
            });
        } catch (error) {
            console.error('[FormDataManager] Erro ao limpar todos os dados:', error);
        }
    }

    // ========================================
    // MÉTODOS PRIVADOS
    // ========================================

    /**
     * Gera chave única para storage
     */
    private generateStorageKey(formName: string): string {
        const currentPath = this.router.url;
        const sanitizedPath = currentPath.replace(/[^a-zA-Z0-9-_]/g, '_');
        return `form_${formName}_${sanitizedPath}`;
    }

    /**
     * Extrai nome do formulário da chave
     */
    private extractFormNameFromKey(storageKey: string): string {
        const parts = storageKey.split('_');
        return parts[1] || 'unknown';
    }

    /**
     * Obtém opções de auto-save
     */
    private getAutoSaveOptions(config: FormDataConfig): AutoSaveOptions {
        return {
            enabled: config.enableAutoSave ?? true,
            delay: config.autoSaveDelay ?? 1000,
            debounceTime: Math.min(config.autoSaveDelay ?? 1000, 500), // Máximo 500ms para debounce
        };
    }
}
